/**
 * Cloudflare Turnstile token validation helper for BFF server routes.
 *
 * Cloudflare's integration guidance is that a siteverify response is only
 * trustworthy when THREE things hold, not one: `success` is true, the `action`
 * baked into the token matches the surface the token is being spent on, and the
 * `hostname` it was solved on is one of ours. Until VARS-112 only `success` was
 * checked, which leaves two real gaps:
 *
 *   - Without the action check, a token minted on a cheap surface (the login
 *     widget) can be spent on an expensive one (the lead endpoint). Turnstile
 *     tokens are not scoped to a form by themselves; `action` is what scopes
 *     them.
 *   - Without the hostname check, a token solved on any other site using this
 *     same sitekey is accepted here.
 *
 * Tokens are also single-use. A spent token fails with `timeout-or-duplicate`,
 * which is why every form calls `reset()` on the widget after a submission
 * rather than reusing the token it already has.
 */

/** The surfaces that can mint a token, and the endpoints that may spend one. */
export const TURNSTILE_ACTIONS = {
  /** Every lead form — they all POST /api/quotes. */
  quote: "quote",
  newsletter: "newsletter",
  login: "login",
  register: "register",
  passwordReset: "password-reset",
} as const;

export type TurnstileAction = (typeof TURNSTILE_ACTIONS)[keyof typeof TURNSTILE_ACTIONS];

export interface TurnstileVerificationResult {
  success: boolean;
  error?: string;
}

interface SiteverifyResponse {
  success: boolean;
  action?: string;
  hostname?: string;
  "error-codes"?: string[];
}

/**
 * Hostnames a token may legitimately have been solved on.
 *
 * Built from `ALLOWED_ORIGINS`, which already answers "which origins are ours"
 * for CORS, rather than introducing a second list that can drift from it.
 *
 * Localhost is admitted only outside production. Cloudflare is explicit that a
 * production backend must not accept it, and the reason is concrete: a
 * development sitekey configured to allow localhost would otherwise let a token
 * solved on an attacker's own machine be spent against the live site.
 */
function allowedHostnames(): string[] {
  const hosts = (process.env.ALLOWED_ORIGINS || "")
    .split(",")
    .map((origin) => origin.trim())
    .filter(Boolean)
    .map((origin) => {
      try {
        return new URL(origin).hostname;
      } catch {
        // Tolerate a bare hostname; ALLOWED_ORIGINS is hand-edited config.
        return origin.replace(/^https?:\/\//, "").split("/")[0];
      }
    })
    .filter(Boolean);

  if (process.env.NODE_ENV !== "production") {
    hosts.push("localhost", "127.0.0.1");
  }
  return hosts;
}

/**
 * Verify a Turnstile token for a specific surface.
 *
 * `expectedAction` is supplied by the route, never taken from the request body:
 * letting the caller declare which action to check for would make the check
 * meaningless, since a forged request would simply declare the action it has.
 *
 * `require` decides what a MISSING token means, and the two surfaces answer
 * differently on purpose:
 *
 * - **Lead forms pass `false`.** A missing token is allowed through; a token
 *   that is present is still verified and still rejected if it is invalid. The
 *   owner's call, September 2026, with no spam observed and enquiries lost for
 *   two weeks to a widget that would not issue tokens: an unprotected enquiry
 *   is worth more than a refused one. Note what this is NOT — it is not "skip
 *   Turnstile"; a bot that supplies a bad token is still turned away, and the
 *   day spam appears, `TURNSTILE_REQUIRE_TOKEN=true` restores strictness
 *   without a deploy.
 * - **Auth routes leave it `true`.** Login, registration and password reset
 *   are a different risk: credential stuffing is automated by definition, and
 *   losing a sign-in attempt costs a retry rather than a customer.
 */
export async function verifyTurnstileToken(
  token: string | undefined,
  clientIp?: string,
  expectedAction?: TurnstileAction,
  options?: { require?: boolean },
): Promise<TurnstileVerificationResult> {
  const secretKey = process.env.TURNSTILE_SECRET_KEY;
  // A single environment variable puts every relaxed surface back to strict,
  // which is what makes this reversible the moment spam starts.
  const required = options?.require ?? true;
  const enforceMissing = required || process.env.TURNSTILE_REQUIRE_TOKEN === "true";

  // In local development or staging where TURNSTILE_SECRET_KEY is not configured,
  // do not block form submissions.
  //
  // This is a deliberate fail-OPEN and it is the one place this module trades
  // safety for availability: losing every enquiry is worse for the business than
  // running unprotected for as long as it takes someone to notice. It is loud in
  // production rather than silent, because "no bot protection" should never be
  // something the site does quietly (VARS-105 found the site in exactly this
  // state and nothing had reported it).
  if (!secretKey) {
    if (process.env.NODE_ENV === "production") {
      console.error(
        "[Turnstile] TURNSTILE_SECRET_KEY is not set in production — form " +
          "submissions are being accepted WITHOUT bot verification.",
      );
    }
    return { success: true };
  }

  if (!token || typeof token !== "string" || token.trim() === "") {
    if (!enforceMissing) {
      // Visible, because "bot protection is not running on this request" must
      // never be something the site concludes quietly — the same reasoning as
      // the missing-secret branch above.
      console.warn(
        "[Turnstile] No token on a relaxed surface — accepting the submission " +
          "without bot verification. Set TURNSTILE_REQUIRE_TOKEN=true to enforce.",
      );
      return { success: true };
    }
    return {
      success: false,
      error: "Bot verification token is missing. Please complete the security check.",
    };
  }

  try {
    const formData = new URLSearchParams();
    formData.append("secret", secretKey);
    formData.append("response", token);
    if (clientIp) {
      formData.append("remoteip", clientIp);
    }

    const response = await fetch("https://challenges.cloudflare.com/turnstile/v0/siteverify", {
      method: "POST",
      body: formData,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (!response.ok) {
      console.error(
        "[Turnstile] HTTP error from siteverify:",
        response.status,
        response.statusText,
      );
      return {
        success: false,
        error: "Verification service temporarily unavailable. Please try again.",
      };
    }

    const data = (await response.json()) as SiteverifyResponse;

    if (!data.success) {
      // `timeout-or-duplicate` here means a token was replayed — the widget was
      // not reset after a previous submission. Logged distinctly because it is
      // an integration bug on our side, not an attack.
      console.warn("[Turnstile] Verification failed:", data["error-codes"]);
      return {
        success: false,
        error: "Security verification failed. Please try again.",
      };
    }

    if (expectedAction && data.action !== expectedAction) {
      console.warn(
        `[Turnstile] Action mismatch: token was minted for "${data.action}", spent on "${expectedAction}".`,
      );
      return {
        success: false,
        error: "Security verification failed. Please try again.",
      };
    }

    const hosts = allowedHostnames();
    // An empty allowlist means ALLOWED_ORIGINS is unset. Skipping the check is
    // the right call there rather than rejecting everything: the deployment is
    // misconfigured, and this module must not be the thing that takes the site
    // down over it. The CORS layer already refuses cross-origin browser writes.
    if (hosts.length > 0 && data.hostname && !hosts.includes(data.hostname)) {
      console.warn(
        `[Turnstile] Hostname mismatch: token solved on "${data.hostname}", which is not in ALLOWED_ORIGINS.`,
      );
      return {
        success: false,
        error: "Security verification failed. Please try again.",
      };
    }

    return { success: true };
  } catch (err) {
    console.error("[Turnstile] Network error during verification:", err);
    return {
      success: false,
      error: "Unable to verify security challenge. Please try again.",
    };
  }
}
